import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { linkPage } from "@utils/router";

import { useQuery } from "@tanstack/react-query";
import { HomeService } from "@services/home.service";
import { useStudentBio } from "@hooks/use_student_bio";
import { useStudentBiography } from "@hooks/use_student_biography";
import MaleLogo from "@assets/images/avatar.png";
import FemaleLogo from "@assets/images/avatar-female.png";
import { Spiner } from "@components/loaders/spiner";
import { toRp } from "@utils/helper";
import { Mading } from "@services/dtos/home.dto";
import { useThemeStore } from "@stores/theme.store";
import { useInboxStore } from "@stores/inbox.store";
// import { androidIsAvailableBiometric } from "@utils/android";
import moment from "moment";

import {
  LuAlertTriangle,
  LuArrowUpRight,
  LuBell,
  LuBookOpen,
  LuCalendarDays,
  LuChevronRight,
  LuCreditCard,
  LuHistory,
  LuLayoutGrid,
  LuStethoscope,
  LuTrophy,
  LuUser,
  LuWallet,
} from "react-icons/lu";
import { PopupError } from "@components/general/popup";

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { setAppBar, setBottomNavigation } = useThemeStore((state) => state);
  const { unreadInbox } = useInboxStore((state) => state);

  const [popupError, setPopupError] = useState<boolean>(false);

  const kategori = "*";
  const { nis, nama } = useStudentBio();
  const { biography, isPending: isPendingBio, gender } = useStudentBiography();

  const avatarSrc =
    isPendingBio || !biography?.foto
      ? gender === "Wanita" || gender === "WANITA"
        ? FemaleLogo
        : MaleLogo
      : `https://ppalmanar.sipond.com/assets/fotosiswa/${biography.foto}`;

  const { data: dataUangSaku, isPending: isPendingUangSaku } = useQuery({
    queryKey: [HomeService.queries.GET_BILLS_AND_BALANCES, nis],
    queryFn: () => HomeService.billsAndBalances({ nis }),
  });

  const { data: dataMading, isPending: isPendingMading } = useQuery({
    queryKey: [HomeService.queries.GET_MADING, kategori, "5"],
    queryFn: () => HomeService.mading(kategori, "5"),
  });

  // Cek ketersediaan biometrik dimatikan sementara, fitur belum diperlukan
  // useEffect(() => {
  //   const setupBiometric = setTimeout(() => {
  //     localStorage.setItem("is-biometric", "false");
  //     androidIsAvailableBiometric();
  //   }, 1000);
  //   return () => clearTimeout(setupBiometric);
  // }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setBottomNavigation("home");
    setAppBar({
      title: "Beranda",
      show: true,
      leading: "home",
      actions: [
        <div
          key="inbox"
          className="tw-relative tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-xl tw-bg-surface-100 tw-cursor-pointer"
          onClick={() => navigate(linkPage.INBOX_PAGE)}
        >
          <LuBell size={14} className="tw-text-primary" />
          {unreadInbox && (
            <span className="tw-absolute tw-top-1 tw-right-1 tw-w-2 tw-h-2 tw-rounded-full tw-bg-red-500" />
          )}
        </div>,
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAppBar, setBottomNavigation, unreadInbox]);

  const openDetailMading = (detailMading: Mading) => {
    navigate(linkPage.MADING_DETAIL, { state: detailMading });
  };

  return (
    <>
      {/* ── Wave Header dengan Balance ── */}
      <div
        className="tw-relative tw-overflow-hidden"
        style={{
          background: "linear-gradient(155deg, #052e16 0%, #166534 70%)",
        }}
      >
        {/* Dekorasi lingkaran */}
        <div
          className="tw-absolute tw-rounded-full tw-pointer-events-none"
          style={{
            background: "#eab308",
            opacity: 0.1,
            width: 220,
            height: 220,
            top: -80,
            right: -55,
          }}
        />
        <div
          className="tw-absolute tw-rounded-full tw-pointer-events-none"
          style={{
            background: "#ffffff",
            opacity: 0.07,
            width: 100,
            height: 100,
            top: 16,
            right: 56,
          }}
        />
        <div
          className="tw-absolute tw-rounded-full tw-pointer-events-none"
          style={{
            background: "#eab308",
            opacity: 0.06,
            width: 60,
            height: 60,
            bottom: 60,
            left: 20,
          }}
        />

        {/* Greeting */}
        <div className="tw-px-5 tw-pt-5 tw-relative tw-z-10">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
            <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0 tw-flex-1">
              <img
                src={avatarSrc}
                alt=""
                className="tw-w-11 tw-h-11 tw-object-cover tw-rounded-full tw-shrink-0"
                style={{ border: "2px solid rgba(255,255,255,0.35)" }}
              />
              <div className="tw-min-w-0">
                <div className="tw-text-[18px] tw-font-bold tw-text-white tw-leading-tight tw-truncate">
                  {biography?.namasiswa ?? nama ?? "---"}
                </div>
              </div>
            </div>
            <div
              className="tw-text-[11px] tw-font-medium tw-px-3 tw-py-1.5 tw-rounded-full tw-shrink-0"
              style={{ background: "rgba(255,255,255,0.18)", color: "#bbf7d0" }}
            >
              {moment().format("DD MMM YYYY")}
            </div>
          </div>
        </div>

        {/* Balance */}
        <div className="tw-px-5 tw-py-5 tw-relative tw-z-10">
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1.5">
            <LuWallet size={13} style={{ color: "#86efac" }} />
            <span
              className="tw-text-xs tw-font-medium"
              style={{ color: "#86efac" }}
            >
              Uang Saku Tersisa
            </span>
          </div>
          <div className="tw-text-[26px] tw-font-bold tw-text-white tw-mb-4 tw-leading-tight">
            {isPendingUangSaku ? (
              <Spiner />
            ) : (
              toRp(dataUangSaku?.data?.saldo ?? 0)
            )}
          </div>
          <div className="tw-flex tw-gap-2.5">
            <button
              onClick={() =>
                navigate(
                  linkPage.FINANCE_REQUEST_VA.replace(":type", "topup"),
                  { state: { tagihan: false, tipe: "topup" } },
                )
              }
              className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2.5 tw-rounded-xl tw-cursor-pointer tw-border-0 tw-text-xs tw-font-semibold"
              style={{ background: "rgba(255,255,255,0.2)", color: "#ffffff" }}
            >
              <LuArrowUpRight size={14} />
              Top-up
            </button>
            <button
              onClick={() => navigate(linkPage.FINANCE_POCKET)}
              className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2.5 tw-rounded-xl tw-cursor-pointer tw-border-0 tw-text-xs tw-font-semibold"
              style={{
                background: "rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.75)",
              }}
            >
              <LuHistory size={14} />
              Riwayat
            </button>
          </div>
        </div>

        {/* SVG Wave */}
        <svg
          viewBox="0 0 390 56"
          preserveAspectRatio="none"
          style={{
            display: "block",
            width: "100%",
            height: 56,
            marginBottom: -1,
          }}
        >
          <path d="M0,56 Q195,0 390,56 L390,56 L0,56 Z" fill="#f8fafc" />
        </svg>
      </div>

      {/* ── Cards Section ── */}
      <div
        className="tw-px-4 tw-relative tw-z-10"
        style={{ marginTop: "-28px" }}
      >
        {/* Tagihan Banner */}
        <div
          className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 tw-rounded-2xl tw-cursor-pointer tw-mb-3"
          style={{
            background: "#fff5f5",
            border: "1px solid #fef2f2",
            boxShadow: "0 2px 12px rgba(239,68,68,0.08)",
          }}
          onClick={() => navigate(linkPage.MENU_FINANCE)}
        >
          <div className="tw-flex-1">
            <div className="tw-text-[11px] tw-text-text-tertiary tw-mb-0.5">
              Total Tagihan Hari Ini
            </div>
            <div className="tw-text-base tw-font-bold tw-text-red-500">
              {isPendingUangSaku ? (
                <Spiner />
              ) : (
                toRp(dataUangSaku?.data?.tagihan ?? 0)
              )}
            </div>
          </div>
          <LuChevronRight className="tw-text-background-400" size={16} />
        </div>

        {/* Menu Card */}
        <div
          className="tw-rounded-2xl tw-bg-white tw-overflow-hidden tw-mb-4"
          style={{
            boxShadow: "0 6px 28px rgba(22,101,52,0.11)",
            border: "1px solid #f0fdf4",
          }}
        >
          {/* Label */}
          <div className="tw-px-4 tw-pt-3 tw-pb-1">
            <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-text-tertiary">
              Menu Cepat
            </span>
          </div>

          {/* Menu Grid */}
          <div className="tw-pb-2 card-body-grid">
            {[
              {
                label: "Req. VA",
                bg: "#f0fdf4",
                color: "#166534",
                Icon: LuCreditCard,
                path: linkPage.FINANCE_REQUEST_VA.replace(":type", "bill"),
                state: { tagihan: true, tipe: "bill" },
              },
              {
                label: "Req. Izin",
                bg: "#fff1f2",
                color: "#f43f5e",
                Icon: LuCalendarDays,
                path: linkPage.PERMIT_PAGE,
              },
              {
                label: "Biodata",
                bg: "#f0fdf4",
                color: "#22c55e",
                Icon: LuUser,
                path: linkPage.INFO_BIOGRAPH,
              },
              {
                label: "Info UKP",
                bg: "#ecfdf5",
                color: "#10b981",
                Icon: LuStethoscope,
                path: linkPage.HEALTH_PAGE,
              },
              {
                label: "Tafaquh",
                bg: "#f5f3ff",
                color: "#7c3aed",
                Icon: LuBookOpen,
                path: "/school/tafaquh",
              },
              {
                label: "Pelanggaran",
                bg: "#fff1f2",
                color: "#ef4444",
                Icon: LuAlertTriangle,
                path: linkPage.VIOLATION_PAGE,
              },
              {
                label: "Prestasi",
                bg: "#fffbeb",
                color: "#f59e0b",
                Icon: LuTrophy,
                path: linkPage.ACHIEVEMENT_PAGE,
              },
              {
                label: "Lainnya",
                bg: "#f8fafc",
                color: "#64748b",
                Icon: LuLayoutGrid,
                path: "/menu/other-menu",
              },
            ].map(({ label, bg, color, Icon, path, state }) => (
              <div key={label} className="tw-mx-2 tw-pb-2 card-grid">
                <div
                  onClick={() => navigate(path, state ? { state } : undefined)}
                  className="tw-m-0"
                >
                  <div
                    className="tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-1.5 tw-rounded-2xl tw-w-[44px] tw-h-[44px]"
                    style={{ background: bg }}
                  >
                    <Icon className="tw-text-xl" style={{ color }} />
                  </div>
                  <div className="tw-line-clamp-1 card-name">{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mading */}
      {isPendingMading ? (
        <div className="tw-mt-5 tw-py-3 tw-m-auto tw-text-center">
          <Spiner />
        </div>
      ) : (dataMading?.data ?? []).length === 0 ? (
        <></>
      ) : (() => {
        const sortedMading = (dataMading?.data ?? [])
          .sort((a, b) => moment(b.tanggal).diff(moment(a.tanggal)))
          .slice(0, 4);
        const [featured, ...rest] = sortedMading;
        return (
          <div className="tw-px-4 tw-pb-20 tw-mt-2">
            <div className="tw-pt-4 tw-pb-3 tw-flex tw-justify-between tw-items-center">
              <div className="tw-text-base tw-font-bold tw-text-text-primary">
                Informasi Terkini
              </div>
              <div
                onClick={() => navigate("/mading/list")}
                className="tw-text-xs tw-font-medium tw-text-accent-primary tw-cursor-pointer"
              >
                Lihat Semua
              </div>
            </div>

            {/* Featured card */}
            <div
              className="tw-relative tw-rounded-2xl tw-overflow-hidden tw-cursor-pointer tw-mb-3"
              style={{ height: 190 }}
              onClick={() => openDetailMading(featured)}
            >
              <img
                src={featured.gambar}
                alt={featured.judul}
                className="tw-w-full tw-h-full tw-object-cover"
              />
              <div
                className="tw-absolute tw-inset-0"
                style={{
                  background:
                    "linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(5,46,22,0.82) 100%)",
                }}
              />
              <span
                className="tw-absolute tw-top-3 tw-left-3 tw-text-[10px] tw-font-bold tw-px-2.5 tw-py-1 tw-rounded-full"
                style={{ background: "#eab308", color: "#052e16" }}
              >
                {moment(featured.tanggal).format("DD MMM YYYY")}
              </span>
              <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-p-4">
                <div className="tw-text-sm tw-font-bold tw-text-white tw-leading-snug tw-line-clamp-2">
                  {featured.judul}
                </div>
              </div>
            </div>

            {/* Article list */}
            {rest.length > 0 && (
              <div className="tw-space-y-2">
                {rest.map((mading, idx) => (
                  <div
                    key={idx}
                    className="tw-flex tw-items-center tw-gap-3 tw-cursor-pointer tw-rounded-xl tw-p-3"
                    style={{
                      background: "#ffffff",
                      border: "1px solid #f0fdf4",
                      boxShadow: "0 1px 6px rgba(22,101,52,0.06)",
                    }}
                    onClick={() => openDetailMading(mading)}
                  >
                    <img
                      src={mading.gambar}
                      alt={mading.judul}
                      className="tw-w-[60px] tw-h-[60px] tw-rounded-xl tw-object-cover tw-shrink-0"
                    />
                    <div className="tw-min-w-0 tw-flex-1">
                      <div
                        className="tw-text-[10px] tw-font-medium tw-mb-1"
                        style={{ color: "#166534" }}
                      >
                        {moment(mading.tanggal).format("DD MMM YYYY")}
                      </div>
                      <div className="tw-text-sm tw-font-semibold tw-text-text-primary tw-line-clamp-2 tw-leading-tight">
                        {mading.judul}
                      </div>
                    </div>
                    <LuChevronRight
                      className="tw-text-background-400 tw-shrink-0"
                      size={14}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}
      {/* end of Mading */}

      <PopupError
        open={popupError}
        title="Fitur Tidak Tersedia"
        message="Fitur ini sedang dalam perbaikan. Kami akan segera mengaktifkannya kembali."
        textConfirm="Tutup"
        onConfirm={() => setPopupError(false)}
      />
    </>
  );
};
